/**
 * Public profile and player search via TanStack Query (docs/APP_PLAN.md
 * §5.2, §12 M4), following the same pattern as `lib/auth.ts`/`lib/games.ts`.
 */
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { PublicProfile, SearchResult } from '@perplexicon/api-client';

import { apiClient } from './api-client.js';

/**
 * Prefix shared by every cached public profile. Exported so mutations that
 * change how an account looks can invalidate *all* of them without knowing
 * which usernames happen to be cached — see `lib/auth.ts`.
 */
export const PROFILE_QUERY_KEY_PREFIX = ['users', 'profile'] as const;

export function profileQueryKey(username: string) {
  return [...PROFILE_QUERY_KEY_PREFIX, username.toLowerCase()] as const;
}

/** `GET /api/users/:username`. `NOT_FOUND` covers both a nonexistent username and a blocked-pair profile (APP_SPEC §13). */
export function useUserProfile(username: string | undefined): UseQueryResult<PublicProfile> {
  return useQuery({
    queryKey: profileQueryKey(username ?? ''),
    queryFn: () => apiClient.users.getProfile(username as string),
    enabled: username !== undefined,
  });
}

export function userSearchQueryKey(query: string) {
  return ['users', 'search', query.toLowerCase()] as const;
}

/** `GET /api/users/search`. Only fetches once `query` is non-empty. */
export function useUserSearch(query: string): UseQueryResult<SearchResult[]> {
  const trimmed = query.trim();
  return useQuery({
    queryKey: userSearchQueryKey(trimmed),
    queryFn: () => apiClient.users.search(trimmed),
    enabled: trimmed.length > 0,
  });
}
